import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {findTimeZone} from '../../../../helpers/dateTimeHelpers';
import {ActionBase, ActionWithPayload} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsColumnNamesModel,
  AnalyticsCreateExportOptionsModel,
  AnalyticsExportModel,
  AnalyticsExportsModel,
  ApiAnalyticsColumnNames,
  ApiAnalyticsEventTypes,
  ApiAnalyticsExports,
  ApiCreateAnalyticsExport,
} from '../../../../models/api/analyticsExportModel';
import {fetchApiAsync, postApiAsync} from '../../../../services/http/http';
import {HttpError} from '../../../../services/http/httpError';
import {CallApiFailedAction} from '../../../api/apiActions';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {getCompany, getCompanyUrl} from '../../../data/company/companySelectors';
import {buildMetricDetailsFilters} from '../analyticsPanelHelpers';
import {
  getAnalyticsDatetimes,
  getAnalyticsSelectedNamespace,
} from '../parameters/analyticsPanelParametersSelectors';

/*
 * Type enum.
 */

export enum AnalyticsPanelExportsActionsEnum {
  FETCH_COLUMN_NAMES = '@@panels/analytics/exports/FETCH_COLUMN_NAMES',
  FETCH_COLUMN_NAMES_COMPLETED = '@@panels/analytics/exports/FETCH_COLUMN_NAMES_COMPLETED',
  FETCH_COLUMN_NAMES_FAILED = '@@panels/analytics/exports/FETCH_COLUMN_NAMES_FAILED',
  FETCH_EVENT_TYPES = '@@panels/analytics/exports/FETCH_EVENT_TYPES',
  FETCH_EVENT_TYPES_COMPLETED = '@@panels/analytics/exports/FETCH_EVENT_TYPES_COMPLETED',
  FETCH_EVENT_TYPES_FAILED = '@@panels/analytics/exports/FETCH_EVENT_TYPES_FAILED',
  FETCH_EXPORTS = '@@panels/analytics/exports/FETCH_EXPORTS',
  FETCH_EXPORTS_COMPLETED = '@@panels/analytics/exports/FETCH_EXPORTS_COMPLETED',
  FETCH_EXPORTS_FAILED = '@@panels/analytics/exports/FETCH_EXPORTS_FAILED',
  EXPORT_UPDATED_RECEIVED = '@@panels/analytics/exports/EXPORT_UPDATED_RECEIVED',
}

/*
 * Types.
 */

interface FetchColumnNamesAction extends ActionBase {
  type: AnalyticsPanelExportsActionsEnum.FETCH_COLUMN_NAMES;
}

interface FetchColumnNamesCompletedAction extends ActionWithPayload<AnalyticsColumnNamesModel> {
  type: AnalyticsPanelExportsActionsEnum.FETCH_COLUMN_NAMES_COMPLETED;
}

interface FetchColumnNamesFailedAction extends CallApiFailedAction<{}> {
  type: AnalyticsPanelExportsActionsEnum.FETCH_COLUMN_NAMES_FAILED;
}

interface FetchEventTypesAction extends ActionBase {
  type: AnalyticsPanelExportsActionsEnum.FETCH_EVENT_TYPES;
}

interface FetchEventTypesCompletedAction extends ActionWithPayload<ReadonlyArray<string>> {
  type: AnalyticsPanelExportsActionsEnum.FETCH_EVENT_TYPES_COMPLETED;
}

interface FetchEventTypesFailedAction extends CallApiFailedAction<{}> {
  type: AnalyticsPanelExportsActionsEnum.FETCH_EVENT_TYPES_FAILED;
}

interface FetchExportsAction extends ActionBase {
  type: AnalyticsPanelExportsActionsEnum.FETCH_EXPORTS;
}

interface FetchExportsCompletedAction extends ActionWithPayload<AnalyticsExportsModel> {
  type: AnalyticsPanelExportsActionsEnum.FETCH_EXPORTS_COMPLETED;
}

interface FetchExportsFailedAction extends CallApiFailedAction<{}> {
  type: AnalyticsPanelExportsActionsEnum.FETCH_EXPORTS_FAILED;
}

interface ExportUpdatedReceivedAction extends ActionWithPayload<AnalyticsExportModel> {
  type: AnalyticsPanelExportsActionsEnum.EXPORT_UPDATED_RECEIVED;
}

/*
 * Action creators.
 */

export function fetchAnalyticsColumnNames(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const path = `${company.url}/exports/columns`;

    dispatch<FetchColumnNamesAction>({
      type: AnalyticsPanelExportsActionsEnum.FETCH_COLUMN_NAMES,
    });

    try {
      // Initiate a fetch from the network.
      const fetchColumnNamesPromise = fetchApiAsync(ApiAnalyticsColumnNames, path);
      const columnNames = await fetchColumnNamesPromise;

      dispatch<FetchColumnNamesCompletedAction>({
        type: AnalyticsPanelExportsActionsEnum.FETCH_COLUMN_NAMES_COMPLETED,
        payload: columnNames,
      });
    } catch (error) {
      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchColumnNamesFailedAction>({
        type: AnalyticsPanelExportsActionsEnum.FETCH_COLUMN_NAMES_FAILED,
        payload: {
          error,
        },
      });
    }
  };
}

export function fetchAnalyticsEventTypes(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const path = `${companyUrl}/exports/event_types`;

    dispatch<FetchEventTypesAction>({
      type: AnalyticsPanelExportsActionsEnum.FETCH_EVENT_TYPES,
    });

    try {
      // Initiate a fetch from the network.
      const fetchEventTypesPromise = fetchApiAsync(ApiAnalyticsEventTypes, path);
      const eventTypes = await fetchEventTypesPromise;

      dispatch<FetchEventTypesCompletedAction>({
        type: AnalyticsPanelExportsActionsEnum.FETCH_EVENT_TYPES_COMPLETED,
        payload: eventTypes,
      });
    } catch (error) {
      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchEventTypesFailedAction>({
        type: AnalyticsPanelExportsActionsEnum.FETCH_EVENT_TYPES_FAILED,
        payload: {
          error,
        },
      });
    }
  };
}

export function fetchAnalyticsExports(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const path = `${companyUrl}/exports`;

    dispatch<FetchExportsAction>({
      type: AnalyticsPanelExportsActionsEnum.FETCH_EXPORTS,
    });

    try {
      // Initiate a fetch from the network.
      const fetchExportsPromise = fetchApiAsync(ApiAnalyticsExports, path);
      const exports = await fetchExportsPromise;

      dispatch<FetchExportsCompletedAction>({
        type: AnalyticsPanelExportsActionsEnum.FETCH_EXPORTS_COMPLETED,
        payload: exports,
      });
    } catch (error) {
      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchExportsFailedAction>({
        type: AnalyticsPanelExportsActionsEnum.FETCH_EXPORTS_FAILED,
        payload: {
          error,
        },
      });
    }
  };
}

export function createAnalyticsExportAsyncAction(
  exportOptions: AnalyticsCreateExportOptionsModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const selectedNamespace = getAnalyticsSelectedNamespace(state);

    const [startDate, endDate] = getAnalyticsDatetimes(state);

    const tz = findTimeZone();

    const payload = {
      period: {
        start: startDate,
        end: endDate,
        tz,
      },
      ...exportOptions,
    };

    if (exportOptions.metric_details) {
      payload.filters = buildMetricDetailsFilters(state, exportOptions.filters);
    }

    try {
      const path = `${companyUrl}/namespaces/${selectedNamespace}/exports`;
      await postApiAsync(ApiCreateAnalyticsExport, path, payload);

      dispatch(fetchAnalyticsExports());
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function exportUpdatedReceivedAction(exportItem: AnalyticsExportModel): ThunkAction {
  return (dispatch) => {
    const action: ExportUpdatedReceivedAction = {
      type: AnalyticsPanelExportsActionsEnum.EXPORT_UPDATED_RECEIVED,
      payload: exportItem,
    };

    return dispatch(action);
  };
}

/*
 * Union type.
 */

type FetchColumnNamesActions =
  | FetchColumnNamesAction
  | FetchColumnNamesCompletedAction
  | FetchColumnNamesFailedAction;

type FetchEventTypesActions =
  | FetchEventTypesAction
  | FetchEventTypesCompletedAction
  | FetchEventTypesFailedAction;

type FetchExportsActions = FetchExportsAction | FetchExportsCompletedAction | FetchExportsFailedAction;

export type AnalyticsPanelExportsAction =
  | FetchColumnNamesActions
  | FetchEventTypesActions
  | FetchExportsActions
  | ExportUpdatedReceivedAction;
